import type { useTranslate } from "@/lib/i18n";
import type { CommissionBase, InvoiceChannel } from "@/lib/api/types";

type Translate = ReturnType<typeof useTranslate>;

/**
 * WHAT TO CALL A BILL'S PLACE (F1, B44) — one answer, one file.
 *
 * Before unit 4 every bill had exactly one table and every screen printed its
 * name. All three cardinalities are now real, and each needs a different word:
 *
 *  · ZERO tables. The bill is takeaway or delivery, and the honest label is the
 *    CHANNEL. Printing nothing would make a delivery read as a missing table,
 *    and printing a dash would make it read as broken.
 *  · ONE. Exactly what every screen printed before, unchanged — which is every
 *    bill in the database when this shipped.
 *  · MANY. A party across two tables, joined. Showing the first alone would
 *    send a waiter to the wrong half of the party.
 *
 * SHARED rather than spelled at each call site, and that is the lesson unit 5
 * wrote down: two screens computing one label by two rules is the divergence
 * unit 1 spent itself removing from the money helpers. Five screens read this —
 * the floor, the two invoice views, the history list and the kitchen board —
 * and a bill has to be called the same thing on all of them.
 */
export function billPlaceLabel(
  invoice: { tableIds: readonly string[]; channel: InvoiceChannel },
  tableName: (tableId: string) => string,
  t: Translate,
): string {
  if (invoice.tableIds.length > 0) {
    return invoice.tableIds.map(tableName).join(" + ");
  }
  return channelLabel(invoice.channel, t);
}

/**
 * THE SETTLE TITLE ON A TABLE'S BILL (audit round 2, 5.1). On a shared tablet
 * «تحصيل الصالة 1» / «تحصيل الفاتورة» does not say whose money is being taken;
 * a dine-in bill seated at a table names it — «تحصيل حساب طاولة الصالة 1» —
 * as the off-premise sheet already names the bag. `null` for every other
 * bill (no table, or not dine-in): the caller keeps its own title.
 * `tableName` is `billPlaceLabel`'s answer for this bill.
 */
export function settleTableTitle(
  invoice: { tableIds: readonly string[]; channel: InvoiceChannel },
  tableName: string,
  t: Translate,
): string | null {
  if (invoice.channel !== "DINE_IN" || invoice.tableIds.length === 0) {
    return null;
  }
  return invoice.tableIds.length > 1
    ? t((m) => m.pos.settleTitleTables, { name: tableName })
    : t((m) => m.pos.settleTitleTable, { table: tableName });
}

/**
 * The channel's own word, for a chip or a column of its own.
 *
 * An exhaustive `switch` with **no `default`**, and the missing default is the
 * point. It used to end `default: return channelDineIn`, which is correct for
 * the three channels that exist and is the WRONG SHAPE for a fourth: a new
 * channel would not read as unknown, it would read as **dine-in** — a confident
 * wrong answer on the five screens `billPlaceLabel` feeds, including the
 * kitchen chit and the printed bill. `storeKind` already reserves `DIGITAL`, so
 * a fourth channel is not hypothetical.
 *
 * With no default the compiler refuses to build until the new member has a word
 * here — the same rule `voidReasonLabel` and `shiftExceptionLabel` already
 * state in their own headers, and the one `afterCloseCommandLabel` was given
 * after nineteen command types spent a release printing as raw tokens (B109).
 */
export function channelLabel(channel: InvoiceChannel, t: Translate): string {
  switch (channel) {
    case "DELIVERY":
      return t((m) => m.pos.channelDelivery);
    case "TAKEAWAY":
      return t((m) => m.pos.channelTakeaway);
    case "DINE_IN":
      return t((m) => m.pos.channelDineIn);
  }
}

/**
 * WHO THE OFF-PREMISE CUSTOMER IS (B9, B102), as the screens print it.
 *
 * `OffPremiseDialog` asks for a name, a phone and — on a delivery — an address,
 * and for the first two days after it shipped **not one of those three reached
 * any screen at all**. They were captured, stored, served on the bill read, and
 * rendered nowhere: not the till panel, not the kitchen chit, not the printed
 * receipt, not the history list, not the back-office bill detail. A shop took a
 * delivery order and nobody in the building could read back where it was going.
 *
 * One helper rather than three screens each deciding what to show, for the
 * reason `billPlaceLabel` above gives: a bill has to be called the same thing
 * everywhere it is named.
 *
 * Returns `[]` on a dine-in bill and on an off-premise bill nobody typed
 * anything into — the caller renders nothing rather than an empty heading.
 */
export function billCustomerLines(invoice: {
  channel: InvoiceChannel;
  customerName: string | null;
  customerPhone: string | null;
  deliveryAddress: string | null;
}): { key: "name" | "phone" | "address"; value: string }[] {
  if (invoice.channel === "DINE_IN") {
    return [];
  }
  const lines: { key: "name" | "phone" | "address"; value: string }[] = [];
  if (invoice.customerName) {
    lines.push({ key: "name", value: invoice.customerName });
  }
  if (invoice.customerPhone) {
    lines.push({ key: "phone", value: invoice.customerPhone });
  }
  // The address is a DELIVERY question. On a takeaway bag it has no meaning,
  // and a stored one could only be left over from a channel this bill is not.
  if (invoice.channel === "DELIVERY" && invoice.deliveryAddress) {
    lines.push({ key: "address", value: invoice.deliveryAddress });
  }
  return lines;
}

/**
 * A DELIVERY WITH NO WAY TO REACH THE CUSTOMER.
 *
 * Every field on the off-premise dialog is optional, and deliberately: a
 * takeaway customer who will not give a name still gets served, and a bill that
 * refused to open without one would send the waiter straight back to the dummy
 * table the whole feature exists to end.
 *
 * But a DELIVERY missing a phone OR an address is an order nobody can deliver
 * — a rider with an address and no phone cannot find flat 3, and one with a
 * phone and no address has nowhere to drive to. It used to warn only when
 * BOTH were blank (T11, 2026-10-06 widened it), and the dialog let one be
 * started with every field blank and said nothing. This is the warning, not a
 * refusal — the founder's "not a full delivery-management solution" is a fence
 * around the lifecycle, not a licence to take an order that cannot arrive.
 *
 * Trimmed: a phone of three spaces is no phone.
 */
export function isUndeliverable(invoice: {
  channel: InvoiceChannel;
  customerPhone: string | null;
  deliveryAddress: string | null;
}): boolean {
  return undeliverableReason(invoice) !== null;
}

/**
 * WHICH part is missing, so the warning can say so instead of "no phone or
 * address" when one of the two is plainly on the screen. `null` means the
 * delivery can be delivered (or the bill is not a delivery at all).
 */
export function undeliverableReason(invoice: {
  channel: InvoiceChannel;
  customerPhone: string | null;
  deliveryAddress: string | null;
}): "phone" | "address" | "both" | null {
  if (invoice.channel !== "DELIVERY") return null;
  const noPhone = !invoice.customerPhone?.trim();
  const noAddress = !invoice.deliveryAddress?.trim();
  if (noPhone && noAddress) return "both";
  if (noPhone) return "phone";
  if (noAddress) return "address";
  return null;
}

/**
 * The warning's sentence for the reason above — three keys, one place, so the
 * open dialog, the correction dialog and the bill all say the same thing.
 */
export function undeliverableMessage(
  reason: NonNullable<ReturnType<typeof undeliverableReason>>,
  t: Translate,
): string {
  switch (reason) {
    case "phone":
      return t((m) => m.pos.undeliverableWarningNoPhone);
    case "address":
      return t((m) => m.pos.undeliverableWarningNoAddress);
    case "both":
      return t((m) => m.pos.undeliverableWarning);
  }
}

/**
 * WHAT A PLATFORM'S RATE IS TAKEN ON (T15), in words — "the food only" or
 * "the whole order value" — for the settle sheets, the bill detail and the
 * platforms page. One place, because a base named two ways on two screens is
 * the drift that makes a statement unmatchable. Exhaustive, no default, for
 * `channelLabel`'s reason.
 */
export function commissionBaseLabel(base: CommissionBase, t: Translate): string {
  switch (base) {
    case "FOOD":
      return t((m) => m.pos.commissionBaseFood);
    case "TOTAL":
      return t((m) => m.pos.commissionBaseTotal);
  }
}

/**
 * B6 D-39 (client half, P26): what an OWN delivery still lacks before it may be
 * sent to the kitchen or closed — the server's `deliveryContactMissing`
 * (`delivery-contact.policy.ts`) mirrored, so the till refuses before the
 * request instead of after it. Only `DELIVERY` with no platform: a platform's
 * courier is the platform's (D90) and a pickup has nobody to send. Blank or
 * whitespace counts as missing. The server still decides (409
 * `RESTAURANT_DELIVERY_CONTACT_REQUIRED`).
 */
export function deliveryContactMissing(bill: {
  channel: InvoiceChannel;
  aggregatorId: string | null;
  customerPhone: string | null;
  deliveryAddress: string | null;
}): ("PHONE" | "ADDRESS")[] {
  if (bill.channel !== "DELIVERY" || bill.aggregatorId !== null) return [];
  const missing: ("PHONE" | "ADDRESS")[] = [];
  if (!bill.customerPhone?.trim()) missing.push("PHONE");
  if (!bill.deliveryAddress?.trim()) missing.push("ADDRESS");
  return missing;
}
